/* eslint-disable no-param-reassign */
import { useMemo } from 'react';
import _ from 'underscore';
import * as Highcharts from 'highcharts';
import { merge } from 'lodash';
import { formatMetric } from '@bingads-webui-campaign-legacy/performance-metric-formatter/index';

import { formatTooltip } from './formatter/format-tooltip';
import { useChartData, ISeries } from './use-chart-data';
import { staticChartStyleOption } from './static-options';
import { gridLineStyle } from '../styles/line';
import { style } from '../styles/chart';
import { uiNextComparative, uiNextOriginal } from '../styles/color';
import { TAxisLabelsFormatterContextObject, TAxisOptions } from '../types/types';

export function formatTickValue(metric, tickValue, tickInterval) {
  const interval = Math.abs(tickInterval);

  if (interval >= 1000) {
    // When tick interval is >= 1000, treat all y-axis values as whole numbers
    return formatMetric(metric, tickValue, {
      compact: 'short',
      maximumFractionDigits: metric === 'Spend' ? 0 : null,
      minimumFractionDigits: metric === 'Spend' ? 0 : null,
    });
  }

  if (Math.floor(interval) < interval) {
    // When tick interval is factional value, show the factional digits.
    return formatMetric(metric, tickValue, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  }

  return formatMetric(metric, tickValue, {
    maximumFractionDigits: metric === 'Spend' ? 0 : 2,
    minimumFractionDigits: 0,
  });
}

function getLabel(value, series: ISeries[], isOriginal) {
  const seriesLine: ISeries = _.find(series, line => line.isOriginal === isOriginal);

  return seriesLine.data[value][0];
}

function formatXAxisLabel({
  value,
  isFirst,
  isLast,
  chart,
  axis,
  series,
}) {
  let clsModifier = 'normal';

  if (isFirst) {
    clsModifier = 'first';
  } else if (isLast && chart.yAxis[1]) {
    // only if second yAxis shows, right margin will be added for last label to align with the graph
    clsModifier = 'last';
  }

  const label = getLabel(value, series, axis.series[0].userOptions.isOriginal);
  return `<span class="highcharts-el_label highcharts-el__label--${clsModifier}">${label}</span>`;
}

function tickPositioner({
  dataMin,
  dataMax,
}) {
  return [dataMin, Math.floor((dataMin + dataMax) / 2), dataMax];
}

const chart = {
  style,
};

const isAllLineValuesZero = line => _.every(line.data, point => point[1] === 0);

export function useChartViewOptions({
  i18n,
  selectedMetricIndexes,
  visibleMetrics,
  visibleMetricsDisplayNames,
  performanceTimeSeries,
  granularity,
  hideCurrency,
  TimeZone,
}) {
  const chartSeries: ISeries[] = useChartData({
    performanceTimeSeries,
    i18n,
    selectedMetricIndexes,
    visibleMetrics,
    visibleMetricsDisplayNames,
    granularity,
    TimeZone,
  });

  const isAllValuesZero = useMemo(() => _.every(chartSeries, line => isAllLineValuesZero(line)), [chartSeries]);

  const hasMultipleXAxis = _.some(chartSeries, line => line.xAxis === 1);
  const hasMultipleYAxis = _.some(chartSeries, line => line.yAxis === 1);

  const xAxis: TAxisOptions[] = useMemo(() => ([{
    endOnTick: true,
    labels: {
      enabled: true,
      formatter(this: TAxisLabelsFormatterContextObject) {
        return formatXAxisLabel({
          value: this.value,
          isFirst: this.isFirst,
          isLast: this.isLast,
          chart: this.chart,
          axis: this.axis,
          series: chartSeries,
        });
      },
      style: {
        color: uiNextOriginal,
      },
      useHTML: true,
    },
    showFirstLabel: true,
    showLastLabel: true,
    startOnTick: true,
    tickPositioner(this: { dataMin: number, dataMax: number }) {
      return tickPositioner({
        dataMin: this.dataMin,
        dataMax: this.dataMax,
      });
    },
  }, {
    endOnTick: true,
    labels: {
      enabled: true,
      formatter(this: TAxisLabelsFormatterContextObject) {
        return formatXAxisLabel({
          value: this.value,
          isFirst: this.isFirst,
          isLast: this.isLast,
          chart: this.chart,
          axis: this.axis,
          series: chartSeries,
        });
      },
      style: {
        color: uiNextComparative,
      },
      reserveSpace: false,
      useHTML: true,
    },
    lineWidth: 0,
    showFirstLabel: true,
    showLastLabel: true,
    startOnTick: true,
    tickPositioner(this: { dataMin: number, dataMax: number }) {
      return tickPositioner({
        dataMin: this.dataMin,
        dataMax: this.dataMax,
      });
    },
  }]), [chartSeries]);

  const hideYAxisLabels = selectedMetricIndexes.length > 2;
  const yAxis: TAxisOptions[] = useMemo(() => ([{
    // Need to show the first y axis to show the grid lines.
    // Instead just set labels empty when we need to hide it.
    labels: {
      formatter(this: TAxisLabelsFormatterContextObject) {
        if (hideYAxisLabels) {
          return '';
        }

        return formatTickValue(visibleMetrics[selectedMetricIndexes[0]], this.value, this.axis.tickInterval);
      },
    },
    ...gridLineStyle,
  }, {
    labels: {
      formatter(this: TAxisLabelsFormatterContextObject) {
        return formatTickValue(visibleMetrics[selectedMetricIndexes[1]], this.value, this.axis.tickInterval);
      },
    },
    visible: !hideYAxisLabels,
    ...gridLineStyle,
  }, {
    visible: false,
  }, {
    visible: false,
  }]), [hideYAxisLabels, visibleMetrics, selectedMetricIndexes]);

  const tooltip = useMemo(() => ({
    formatter(this: { points: any[] }) {
      return formatTooltip({
        points: this.points,
        hideCurrency,
      });
    },
    borderWidth: 1,
    outside: false,
    crosshairs: [{
      color: 'rgba(0,0,0,0.1)',
    }],
  }), [hideCurrency]);

  const chartStyleOption = useMemo(() => {
    const x = hasMultipleXAxis
      ? staticChartStyleOption.xAxis
      : _.first(staticChartStyleOption.xAxis, 1);
    let y = hasMultipleYAxis
      ? staticChartStyleOption.yAxis
      : _.first(staticChartStyleOption.yAxis, 1);
    if (isAllValuesZero) {
      y = _.map(y, axis => ({
        ...axis,
        min: 0,
        max: 1,
        tickInterval: 0.5,
      }));
    }
    return {
      ...staticChartStyleOption,
      xAxis: x,
      yAxis: y,
    };
  }, [hasMultipleXAxis, hasMultipleYAxis, isAllValuesZero]);

  const highchartsOptions = useMemo(() => ({
    series: chartSeries,
    tooltip,
    xAxis: hasMultipleXAxis ? xAxis : _.first(xAxis, 1) || staticChartStyleOption.xAxis,
    yAxis: hasMultipleYAxis ? yAxis : _.first(yAxis, 1) || staticChartStyleOption.yAxis,
    chart,
  }), [chartSeries, hasMultipleXAxis, hasMultipleYAxis, tooltip, xAxis, yAxis]);

  const result = useMemo(() => ({
    highcharts: Highcharts,
    options: merge({}, chartStyleOption, highchartsOptions),
    hasChartData: highchartsOptions?.series?.length > 0,
  }), [chartStyleOption, highchartsOptions]);

  return result;
}
